import { SENDMESSAGE_FAIL, SENDMESSAGE_SUCCESS } from 'redux/modules/messages';

// Whether this page is running inside the Twine iOS / Android app.
// Seeded on the server from the request user agent (see serverRenderer.js)
// and hydrated on the client, so SSR and first client render agree.
const SET_PLATFORM = 'twineApp/nativeApp/SET_PLATFORM';
const SET_ONLINE = 'twineApp/nativeApp/SET_ONLINE';
const CONTENT_LOADED = 'twineApp/nativeApp/CONTENT_LOADED';
const QUEUE_MESSAGE = 'twineApp/nativeApp/QUEUE_MESSAGE';
const CLEAR_QUEUED_MESSAGE = 'twineApp/nativeApp/CLEAR_QUEUED_MESSAGE';

const initialState = {
  platform: null,
  online: true,
  // When the connection dropped, so the offline banner can say what the
  // cached content is from. Null while online.
  offlineSince: null,
  // When the screen on show last loaded while online: the offline banner
  // says "Showing what loaded …" with this.
  loadedAt: null,
  // Threads (by username) whose draft was sent while offline. The draft
  // itself stays in messages[username].newMessage; MessageThread sends it
  // once that thread is open and the connection is back.
  queuedMessages: {},
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_PLATFORM:
      return {
        ...state,
        platform: action.platform || null,
      };
    case SET_ONLINE:
      if (action.online === state.online) return state;
      return {
        ...state,
        online: action.online,
        offlineSince: action.online ? null : action.at,
      };
    case CONTENT_LOADED:
      // Screens opened offline show what was already loaded: keep the time.
      if (!state.online) return state;
      return {
        ...state,
        loadedAt: action.at,
      };
    case QUEUE_MESSAGE:
      return {
        ...state,
        queuedMessages: { ...state.queuedMessages, [action.username]: true },
      };
    // A queued draft leaves the queue once its send settles. On failure the
    // draft stays in the box and Send works again, so the user retries it.
    case CLEAR_QUEUED_MESSAGE:
    case SENDMESSAGE_SUCCESS:
    case SENDMESSAGE_FAIL: {
      if (!state.queuedMessages[action.username]) return state;
      const queuedMessages = { ...state.queuedMessages };
      delete queuedMessages[action.username];
      return { ...state, queuedMessages };
    }
    default:
      return state;
  }
}

export function setNativePlatform(platform) {
  return {
    type: SET_PLATFORM,
    platform,
  };
}

export function setOnline(online, at = Date.now()) {
  return {
    type: SET_ONLINE,
    online: Boolean(online),
    at,
  };
}

export function markContentLoaded(at = Date.now()) {
  return {
    type: CONTENT_LOADED,
    at,
  };
}

export function queueMessage(username) {
  return { type: QUEUE_MESSAGE, username };
}

export function clearQueuedMessage(username) {
  return { type: CLEAR_QUEUED_MESSAGE, username };
}

export function isMessageQueued(globalState, username) {
  const { nativeApp } = globalState;
  return Boolean(username && nativeApp && nativeApp.queuedMessages?.[username]);
}

export function isNativeApp(globalState) {
  return Boolean(globalState.nativeApp && globalState.nativeApp.platform);
}

export function getNativePlatform(globalState) {
  return (globalState.nativeApp && globalState.nativeApp.platform) || null;
}

/** Offline only counts in the app: the web keeps its existing behaviour. */
export function isAppOffline(globalState) {
  return isNativeApp(globalState) && globalState.nativeApp.online === false;
}

export function getOfflineSince(globalState) {
  return (globalState.nativeApp && globalState.nativeApp.offlineSince) || null;
}

/** When what's on screen loaded: the last load while online. */
export function getContentLoadedAt(globalState) {
  const { nativeApp } = globalState;
  return (nativeApp && (nativeApp.loadedAt || nativeApp.offlineSince)) || null;
}

/**
 * Whether purchase and upgrade surfaces (Stripe Checkout, the Customer
 * Portal, /pricing, credit packs, Boost, payments to freelancers) may render.
 * Never in the app: Apple 3.1.1 bars steering to web payments. TP-392 swaps
 * this for the native paywall, so every such surface goes through here or
 * components/NativeApp/WebOnly.
 */
export function canShowWebPurchase(globalState) {
  return !isNativeApp(globalState);
}
